/**
 * Parses the language code from a given URL path.
 * The segment should be in the format of /v1.0-de/ or /v2.3.4-jp/.
 * If no language code is found, it defaults to "en".
 *
 * @example
 * getLanguageFromPath('/apples-api/v1.0-de/docs/getting-started'); -> "de"
 * getLanguageFromPath('/apples-api/v1.0-test-de/docs/getting-started'); -> "de"
 * getLanguageFromPath('/apples-api/v2.0-fr_branch-name/docs/getting-started'); -> "fr"
 * getLanguageFromPath('/apples-api/docs/getting-started'); -> "en" (default)
 */
export function getLanguageFromPath(path: string): string {
  // Match a segment like /v1.0-de/ or /v2.3.4-jp/
  const match = path.match(/\/v\d+\.\d+(?:\.\d+)?(?:-[a-z]+)*-([a-z]{2})(?=\/|_)/i);
  return match ? match[1].toLowerCase() : 'en';
}

/**
 * Parses the provided version name and returns a hash with `version` and `lang`
 * segments.
 * @example
 * ```ts
 * parseLanguageVersion('1.0-es')
 * // => { version: '1.0', lang: 'es' }
 * parseLanguageVersion('1.0')
 * // => { version: '1.0', lang: 'en' }
 * ```
 */
export const parseLanguageVersion = (
  versionName: string,
): {
  version: string;
  lang: string;
} => {
  // Match version with language code at the end (letters only, no dots)
  const match = versionName.match(/^(.+)-([a-z]{2})$/);

  if (!match) {
    return {
      version: versionName,
      lang: 'en',
    };
  }

  return {
    version: match[1],
    lang: match[2],
  };
};
